import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useNavigate,
} from "react-router-dom";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format, subDays, startOfQuarter, endOfQuarter } from "date-fns";
import Admin from "@/pages/Admin";
import UserManagement from "./pages/userManagement";
import CampaignDetail from "@/pages/CampaignDetail";
import CampaignEdit from "@/pages/CampaignEdit";
import { formatDateOnly, formatCreatedAt } from "./utils/formatDate";
import * as XLSX from "xlsx";
import Signup from "./pages/Signup.js";
import OTP from "./pages/OTP";
import { useTheme } from "@/hooks/useTheme";
import Preferability from "@/pages/Preferability";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import api from "./utils/axios";
import { DESIGNATION_LABELS } from "./utils/designations";
import { hasRole } from "./utils/roles";

// ─── Status Badge ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  live: {
    cls: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700",
  },
  completed: {
    cls: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700",
  },
  complete: {
    cls: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700",
  },
  paused: {
    cls: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700",
  },
  declined: {
    cls: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700",
  },
  "proof submitted": {
    cls: "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700",
  },
};




const StatusBadge = ({ value }) => {
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  const key = String(value).toLowerCase().trim();
  const cfg = STATUS_CONFIG[key];
  const cls = cfg ? cfg.cls : "bg-muted text-muted-foreground border-border";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${cls}`}
    >
      {value}
    </span>
  );
};

// ─── Sparkline SVG (used only on Avg % to Goal card) ─────────────────────────
const Sparkline = ({
  data = [],
  color = "#3b82f6",
  height = 36,
  width = 96,
}) => {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pad = 2;
  const w = width - pad * 2;
  const h = height - pad * 2;

  const points = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * w;
    const y = pad + h - ((v - min) / range) * h;
    return `${x},${y}`;
  });
  const first = points[0].split(",");
  const last = points[points.length - 1].split(",");
  const area = `M${first[0]},${height} L${points.join(" L")} L${last[0]},${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      className="overflow-visible"
    >
      <defs>
        <linearGradient
          id={`sg-${color.replace("#", "")}`}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#sg-${color.replace("#", "")})`} />
      <polyline
        points={points.join(" ")}
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} />
    </svg>
  );
};

// ─── Sort: ONLY these columns are sortable ────────────────────────────────────
const SORTABLE = new Set([
  "deliveryDay",
  "pacing",
  "allocation",
  "accepted",
  "rejected",
  "outstanding",
  "pendingQA",
  "pctToGoal",
  "startDate",
  "endDate",
  "createdAt",
]);

// Options for the "Sort by" dropdown near Customize — dashboard defaults to
// keeping the soonest-ending campaigns at the top.
const SORT_BY_OPTIONS = [
  { value: "endDate", label: "By End Date" },
  { value: "startDate", label: "By Start Date" },
  { value: "createdAt", label: "By Time Added" },
];

const NUMERIC = new Set([
  "allocation",
  "accepted",
  "rejected",
  "outstanding",
  "pendingQA",
  "pctToGoal",
]);

const DATE_COLS = new Set(["liveDate", "startDate", "endDate", "createdAt"]);

const SortIndicator = ({ col, sortConfig }) => {
  if (sortConfig.key !== col)
    return (
      <span className="ml-0.5 text-[9px] text-muted-foreground/30 select-none">
        ⇅
      </span>
    );
  return (
    <span className="ml-0.5 text-[10px] text-primary select-none">
      {sortConfig.dir === "asc" ? "↑" : "↓"}
    </span>
  );
};

// ─── Per-column default widths ─────────────────────────────────────────────────
const COL_DEFAULTS = {
  status: 105,
  campaignName: 180,
  clientName: 100,
  platform: 95,
  deliveryDay: 100,
  pacing: 100,
  allocation: 90,
  accepted: 85,
  rejected: 80,
  outstanding: 100,
  pendingQA: 90,
  pctToGoal: 78,
  priority: 78,
  CM: 153,
  CSM: 110,
  startDate: 110,
  endDate: 110,
  liveDate: 100,
  createdAt: 125,
  __progress__: 85,
};
const DEFAULT_W = 100;
const MIN_W = 55;

// ─── Column resize hook ───────────────────────────────────────────────────────
function useColumnResize() {
  const [widths, setWidths] = useState({});
  const drag = useRef({ active: false, col: null, startX: 0, startW: 0 });

  const getW = useCallback(
    (col) => widths[col] ?? COL_DEFAULTS[col] ?? DEFAULT_W,
    [widths],
  );

  const onMouseDown = useCallback(
    (e, col) => {
      e.preventDefault();
      e.stopPropagation();
      drag.current = {
        active: true,
        col,
        startX: e.clientX,
        startW: widths[col] ?? COL_DEFAULTS[col] ?? DEFAULT_W,
      };

      const move = (ev) => {
        if (!drag.current.active) return;
        const next = Math.max(
          MIN_W,
          drag.current.startW + ev.clientX - drag.current.startX,
        );
        setWidths((p) => ({ ...p, [drag.current.col]: next }));
      };
      const up = () => {
        drag.current.active = false;
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    },
    [widths],
  );

  return { getW, onMouseDown };
}

// ─── Columns hidden by default ────────────────────────────────────────────────
const HIDDEN_BY_DEFAULT = new Set([
  "id",
  "priority",
  "lrmComments",
  "akComments",
  "integrateIG",
  "createdAt",
  "internalCID",
  "liveDate",
  "clientName",
  "pending",
  "CSM",
  "salesManagers",
  "io_po_number",
  "so_number",
  "deletedAt",
  "deletedBy",
]);

const formatColumnLabel = (key) => {
  if (!key) return "";
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const ACCENT = {
  blue: { bar: "bg-blue-500", text: "text-blue-600 dark:text-blue-400" },
  emerald: {
    bar: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
  },
  rose: { bar: "bg-rose-500", text: "text-rose-600 dark:text-rose-400" },
  amber: { bar: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
};

const ACCENT_HEX = {
  blue: "#3b82f6",
  emerald: "#10b981",
  rose: "#f43f5e",
  amber: "#f59e0b",
};

// Professional inline SVG icons — replaces emojis
const KPI_ICONS = {
  allocated: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-[18px] h-[18px]"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  ),
  accepted: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-[18px] h-[18px]"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  deficit: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-[18px] h-[18px]"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  avgPct: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-[18px] h-[18px]"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
};

// iconKey: which icon; showSparkline: only true on the Avg % to Goal card
const KpiCard = ({
  label,
  value,
  sub,
  progress,
  iconKey = "allocated",
  accent = "blue",
  sparkline = [],
  showSparkline = false,
}) => {
  const a = ACCENT[accent];
  const hex = ACCENT_HEX[accent];
  const last = sparkline[sparkline.length - 1] ?? 0;
  const prev = sparkline[sparkline.length - 2] ?? last;
  const trend = last > prev ? "↑" : last < prev ? "↓" : "→";
  const trendCls =
    last > prev
      ? "text-emerald-500"
      : last < prev
        ? "text-rose-500"
        : "text-muted-foreground";

  return (
    <div className="neon-card p-5 flex flex-col gap-2.5">
      {/* Header: label left, icon right */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-muted-foreground font-medium leading-tight">
          {label}
        </p>
        {/* Icon: no background pill — just a clean stroked SVG in accent colour */}
        <span className={`flex-shrink-0 ${a.text}`}>{KPI_ICONS[iconKey]}</span>
      </div>

      {/* Value row — sparkline ONLY on showSparkline card */}
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-3xl font-bold tracking-tight leading-none">
            {value}
          </h2>
          {sub && <p className={`text-xs mt-1 font-medium ${a.text}`}>{sub}</p>}
        </div>
        {showSparkline && sparkline.length >= 2 && (
          <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
            <span className={`text-[10px] font-bold ${trendCls}`}>
              {trend} wk
            </span>
            <Sparkline data={sparkline} color={hex} width={80} height={32} />
          </div>
        )}
      </div>

      {/* Progress bar */}
      {progress !== undefined && (
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] text-muted-foreground">Progress</span>
            <span className={`text-[10px] font-semibold ${a.text}`}>
              {Math.min(100, progress)}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-border overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out ${a.bar}`}
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Quick status filter chips ────────────────────────────────────────────────
const STATUS_CHIPS = [
  { label: "All", value: "ALL", dot: "bg-muted-foreground" },
  { label: "Live", value: "Live", dot: "bg-blue-500" },
  { label: "Completed", value: "Completed", dot: "bg-emerald-500" },
  { label: "Paused", value: "Paused", dot: "bg-orange-500" },
  { label: "Declined", value: "Declined", dot: "bg-red-500" },
  { label: "Proof Submitted", value: "Proof Submitted", dot: "bg-yellow-500" },
];

// ─── Recycle Bin ──────────────────────────────────────────────────────────────
// Admin-only view of soft-deleted campaigns, with restore / permanent-delete.
const RecycleBinDialog = ({ open, onOpenChange, canDelete, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmPermanentId, setConfirmPermanentId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns/recycle-bin");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to load recycle bin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setConfirmPermanentId(null);
      load();
    }
  }, [open]);

  const restore = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/campaigns/${id}/restore`);
      toast.success("Campaign restored");
      setItems((prev) => prev.filter((c) => c.id !== id));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to restore campaign");
    } finally {
      setBusyId(null);
    }
  };

  const permanentlyDelete = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/campaigns/${id}/permanent`);
      toast.success("Campaign permanently deleted");
      setItems((prev) => prev.filter((c) => c.id !== id));
      setConfirmPermanentId(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to permanently delete campaign");
    } finally {
      setBusyId(null);
    }
  };

  if (!canDelete) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="recycle-bin-modal"
        className="max-w-2xl max-h-[75vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Recycle Bin</DialogTitle>
        </DialogHeader>

        {loading && (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        )}
        {!loading && items.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">
            The recycle bin is empty.
          </p>
        )}

        <ul className="space-y-2">
          {items.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border bg-muted/30"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">
                  {c.campaignName || `Campaign #${c.id}`}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  Deleted {formatCreatedAt(c.deletedAt)}
                  {c.deletedBy ? ` by ${c.deletedBy}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="neon-left neon-button"
                  disabled={busyId === c.id}
                  onClick={() => restore(c.id)}
                >
                  Restore
                </Button>
                {confirmPermanentId === c.id ? (
                  <>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busyId === c.id}
                      onClick={() => permanentlyDelete(c.id)}
                    >
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmPermanentId(null)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busyId === c.id}
                    onClick={() => setConfirmPermanentId(c.id)}
                  >
                    Delete Permanently
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
const Dashboard = ({ logout }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({
    totalAllocated: 0,
    totalAccepted: 0,
    totalDeficit: 0,
    avgPctToGoal: 0,
  });
  const [status, setStatus] = useState("Live");
  const [preset, setPreset] = useState("ALL");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selected, setSelected] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [showCustomize, setShowCustomize] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  // Default sort: end date ascending, so campaigns wrapping up soonest sit at the top.
  const [sortConfig, setSortConfig] = useState({ key: "endDate", dir: "asc" });
  const [sparklines, setSparklines] = useState({
    allocated: [],
    accepted: [],
    deficit: [],
    avgPct: [],
  });

  // CM column header filter — multiselect list of CM names to show; empty = show all
  const [cmFilter, setCmFilter] = useState(new Set());
  const [cmFilterOpen, setCmFilterOpen] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showRecycleBin, setShowRecycleBin] = useState(false);

  const { getW, onMouseDown: resizeMouseDown } = useColumnResize();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const role = (user.role || user.designation || "").toLowerCase();
const canAddCampaign    = hasRole(user, "admin", "ceo", "csm");
const canDeleteCampaign = hasRole(user, "admin");

  useEffect(() => {
    applyPreset("ALL");
    fetchSparklines();
  }, []);

  const fetchSparklines = async () => {
    try {
      const { data } = await api.get("/campaigns/sparklines");
      setSparklines(data);
    } catch {
      // silently ignore — sparklines non-critical
    }
  };

  const applyPreset = (p) => {
    const now = new Date();
    let start = null,
      end = null;

    if (p === "THIS_MONTH") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (p === "LAST_30") {
      start = subDays(now, 30);
      end = now;
    } else if (p === "LAST_90") {
      start = subDays(now, 90);
      end = now;
    } else if (p === "THIS_QUARTER") {
      start = startOfQuarter(now);
      end = endOfQuarter(now);
    } else if (p === "CUSTOM") {
      setPreset(p);
      return;
    } else if (p === "ALL") {
      setPreset(p);
      setDateRange({ start: null, end: null });
      getData({ page: 1, overrideDates: { start: null, end: null } });
      return;
    }

    setPreset(p);
    setDateRange({ start, end });
    getData({ page: 1, overrideDates: { start, end } });
  };

  const getData = async (opts = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const usePage = opts.page ?? page;
      const useSize = opts.pageSize ?? pageSize;
      const useStatus = opts.status ?? status;

      params.set("page", usePage);
      params.set("page_size", useSize);
      if (useStatus !== "ALL") params.set("status", useStatus);

      const dr = opts.overrideDates ?? dateRange;
      if (dr?.start) params.set("start", format(dr.start, "yyyy-MM-dd"));
      if (dr?.end) params.set("end", format(dr.end, "yyyy-MM-dd"));

      const { data } = await api.get(`/campaigns/table?${params}`);

      const cols = Array.isArray(data.columns) ? [...data.columns] : [];
      if (!cols.includes("salesManagers")) cols.push("salesManagers");
      if (!cols.includes("clientName")) cols.push("clientName");

      setRows(data.rows || []);
      setColumns(cols);
      setVisibleColumns((prev) =>
        prev.length > 0 ? prev : cols.filter((c) => !HIDDEN_BY_DEFAULT.has(c)),
      );
      setKpis(data.kpis || {});
      setTotal(data.total || (data.rows || []).length);
      setPage(usePage);
      setPageSize(useSize);
      setSelectedRows(new Set());
      setSelectAll(false);
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    const exportData =
      selectedRows.size > 0 ? rows.filter((r) => selectedRows.has(r.id)) : rows;
    if (!exportData.length) {
      alert("No data to export");
      return;
    }
    const formatted = exportData.map((row) => {
      const obj = {};
      visibleColumns.forEach((col) => {
        let val = row[col];
        if (col === "salesManagers") {
          if (typeof val === "string") {
            try {
              const p = JSON.parse(val);
              if (Array.isArray(p)) val = p.join(", ");
            } catch {}
          } else if (Array.isArray(val)) val = val.join(", ");
        }
        if (col === "createdAt") val = formatCreatedAt(val);
        else if (col?.toLowerCase().includes("date")) val = formatDateOnly(val);
        obj[col] = val ?? "";
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Campaigns");
    XLSX.writeFile(
      wb,
      `campaigns_export_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`,
    );
  };

  // Unique CM values present in the current rows, for the column-header filter dropdown
  const uniqueCMs = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.CM) set.add(r.CM);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (searchQuery.trim()) {
      out = out.filter((row) =>
        Object.values(row).some((v) =>
          String(v).toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      );
    }
    if (cmFilter.size > 0) {
      out = out.filter((row) => row.CM && cmFilter.has(row.CM));
    }
    return out;
  }, [rows, searchQuery, cmFilter]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    const { key, dir } = sortConfig;
    const mult = dir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = a[key] ?? "";
      const bv = b[key] ?? "";
      if (NUMERIC.has(key)) return (Number(av) - Number(bv)) * mult;
      if (DATE_COLS.has(key))
        return (
          ((av ? new Date(av).getTime() : 0) -
            (bv ? new Date(bv).getTime() : 0)) *
          mult
        );
      return (
        String(av).toLowerCase().localeCompare(String(bv).toLowerCase()) * mult
      );
    });
  }, [filteredRows, sortConfig]);

  const handleSort = (col) => {
    if (!SORTABLE.has(col)) return;
    setSortConfig((prev) =>
      prev.key === col
        ? { key: col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: col, dir: "asc" },
    );
  };

  const rowCountLabel = useMemo(() => {
    const n = filteredRows.length;
    if (n === 0) return "0 records";
    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, n);
    return `${from}–${to} of ${n} record${n !== 1 ? "s" : ""}`;
  }, [filteredRows.length, page, pageSize]);

  const acceptedPct =
    kpis.totalAllocated > 0
      ? Math.round((kpis.totalAccepted / kpis.totalAllocated) * 100)
      : 0;
  const deficitPct =
    kpis.totalAllocated > 0
      ? Math.round((kpis.totalDeficit / kpis.totalAllocated) * 100)
      : 0;

  const renderCell = (col, row) => {
    const rawVal = row[col] ?? row[(col || "").replace(/\s+/g, "")] ?? "";
    let displayVal = rawVal;

    if (col === "salesManagers") {
      if (typeof rawVal === "string") {
        try {
          const p = JSON.parse(rawVal);
          if (Array.isArray(p)) displayVal = p.join(", ");
        } catch {
          displayVal = rawVal;
        }
      } else if (Array.isArray(rawVal)) displayVal = rawVal.join(", ");
    }
    if (col === "csm") displayVal = rawVal || "—";
    if (col === "clientName")
      displayVal =
        typeof rawVal === "object" && rawVal
          ? rawVal.label || rawVal.name || "—"
          : rawVal || "—";

    const isName =
      col === "Campaign" || col === "Campaign Name" || col === "campaignName";
    if (col === "status") return <StatusBadge value={displayVal} />;
    if (isName)
      return (
        <button
          className="underline text-blue-600 text-left truncate w-full"
          data-testid={`campaign-link-${row.id}`}
          onClick={() => navigate(`/campaign/${row.id}`)}
        >
          {String(displayVal || "—")}
        </button>
      );
    if (col.toLowerCase() === "createdat")
      return <span>{formatCreatedAt(displayVal)}</span>;
    if (col.toLowerCase().includes("date"))
      return <span>{formatDateOnly(displayVal)}</span>;
    return <span className="truncate">{String(displayVal ?? "—")}</span>;
  };

  const validateAndFetchCustomDates = (start, end) => {
    if (!start || !end) return;
    if (end < start) {
      toast.error("End date cannot be before start date");
      return;
    }
    getData({ page: 1, overrideDates: { start, end } });
  };

  const handleBulkDelete = async () => {
  if (deleteConfirmText !== "DELETE") return;
  setDeleting(true);
  try {
    const ids = Array.from(selectedRows);
    await api.post("/campaigns/bulk-delete", { ids });
    toast.success(`${ids.length} campaign${ids.length !== 1 ? "s" : ""} deleted`);
    setShowDeleteDialog(false);
    setDeleteConfirmText("");
    getData({ page: 1 });
  } catch (e) {
    toast.error(e.response?.data?.error || "Failed to delete campaigns");
  } finally {
    setDeleting(false);
  }
};

const formatColumnLabel = (key) => {
  if (!key) return "";
  if (DESIGNATION_LABELS[key]) return DESIGNATION_LABELS[key];
  return key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, (c) => c.toUpperCase());
};

  return (
    <div className="">
      <div className="mx-auto px-8 py-8">
        <Navbar
          showSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          logout={logout}
        />

        {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
        <div className="grid md:grid-cols-4 gap-4 mb-4">
          <KpiCard
            label="Total Allocated"
            value={kpis.totalAllocated.toLocaleString()}
            sub="Total leads allocated across campaigns"
            iconKey="allocated"
            accent="blue"
            sparkline={sparklines.allocated}
          />
          <KpiCard
            label="Total Accepted"
            value={kpis.totalAccepted.toLocaleString()}
            sub={`${acceptedPct}% of total allocation`}
            progress={acceptedPct}
            iconKey="accepted"
            accent="emerald"
            sparkline={sparklines.accepted}
          />
          <KpiCard
            label="Total Deficit"
            value={kpis.totalDeficit.toLocaleString()}
            sub={`${deficitPct}% gap remaining`}
            progress={deficitPct}
            iconKey="deficit"
            accent="rose"
            sparkline={sparklines.deficit}
          />
          <KpiCard
            label="Avg % to Goal"
            value={`${kpis.avgPctToGoal}%`}
            sub="Across all visible campaigns"
            progress={kpis.avgPctToGoal}
            iconKey="avgPct"
            accent="amber"
            sparkline={sparklines.avgPct}
          />
        </div>

        {/* ── Quick-filter status chips ─────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {STATUS_CHIPS.map((chip) => {
            const active = status === chip.value;
            return (
              <button
                key={chip.value}
                onClick={() => {
                  setStatus(chip.value);
                  getData({ page: 1, status: chip.value });
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150
                  ${
                    active
                      ? "border-primary text-foreground bg-primary/10 scale-105 shadow-sm"
                      : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                  }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${chip.dot}`}
                />
                {chip.label}
              </button>
            );
          })}
          <span className="ml-auto text-xs text-muted-foreground">
            {filteredRows.length} campaign{filteredRows.length !== 1 ? "s" : ""}{" "}
            shown
          </span>
        </div>

        {/* ── Filters row ───────────────────────────────────────────────────── */}
        <div className="rounded-2xl p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div>Filters</div>

          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger
              className="w-[180px] neon-left neon-input"
              data-testid="date-preset-select"
            >
              <SelectValue placeholder="Date Preset" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem data-testid="preset-this-month" value="THIS_MONTH">
                This Month
              </SelectItem>
              <SelectItem data-testid="preset-last-30" value="LAST_30">
                Last 30 Days
              </SelectItem>
              <SelectItem data-testid="preset-last-90" value="LAST_90">
                Last 90 Days
              </SelectItem>
              <SelectItem
                data-testid="preset-this-quarter"
                value="THIS_QUARTER"
              >
                This Quarter
              </SelectItem>
              <SelectItem data-testid="preset-custom" value="CUSTOM">
                Custom
              </SelectItem>
              <SelectItem value="ALL">All</SelectItem>
            </SelectContent>
          </Select>

          {preset === "CUSTOM" && (
            <>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="start-date-trigger">
                    {dateRange.start
                      ? `Start: ${format(dateRange.start, "PPP")}`
                      : "Start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateRange.start}
                    onSelect={(d) => {
                      setDateRange((prev) => {
                        const next = { ...prev, start: d };
                        if (prev.end && d && prev.end < d) {
                          toast.error("End date cannot be before start date");
                          next.end = null;
                        }
                        return next;
                      });
                      setStartDateOpen(false);
                      setEndDateOpen(true);
                    }}
                    data-testid="start-date-calendar"
                  />
                </PopoverContent>
              </Popover>

              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="end-date-trigger">
                    {dateRange.end
                      ? `End: ${format(dateRange.end, "PPP")}`
                      : "End date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateRange.end}
                    onSelect={(d) => {
                      if (!dateRange.start) {
                        toast.error("Please select start date first");
                        return;
                      }
                      if (d < dateRange.start) {
                        toast.error("End date cannot be before start date");
                        return;
                      }
                      setDateRange((prev) => ({ ...prev, end: d }));
                      setEndDateOpen(false);
                      validateAndFetchCustomDates(dateRange.start, d);
                    }}
                    data-testid="end-date-calendar"
                  />
                </PopoverContent>
              </Popover>
            </>
          )}

          <Select
            value={sortConfig.key || "endDate"}
            onValueChange={(v) => setSortConfig({ key: v, dir: "asc" })}
          >
            <SelectTrigger
              className="w-[160px] neon-left neon-input"
              data-testid="sort-by-select"
            >
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORT_BY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover open={showCustomize} onOpenChange={setShowCustomize}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="neon-left neon-button">
                Customize
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="bottom"
              className="w-64 max-h-80 overflow-y-auto p-3"
            >
              {columns.map((col) => (
                <label
                  key={col}
                  className="flex items-center gap-2 text-sm py-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(col)}
                    onChange={(e) => {
                      setVisibleColumns((prev) => {
                        if (e.target.checked) {
                          const next = new Set([...prev, col]);
                          return columns.filter((c) => next.has(c));
                        }
                        if (prev.length === 1) {
                          toast.error(
                            "At least one column must remain visible",
                          );
                          return prev;
                        }
                        return prev.filter((c) => c !== col);
                      });
                    }}
                  />
                  <span>{formatColumnLabel(col)}</span>
                </label>
              ))}
            </PopoverContent>
          </Popover>

          {/* was: <Button className="ml-auto neon-left neon-button" ...>Export</Button> + canAddCampaign block */}
          <div className="ml-auto flex items-center gap-2">
            {canDeleteCampaign && (
              <Button
                variant="outline"
                className="neon-left neon-button"
                onClick={() => setShowRecycleBin(true)}
                data-testid="recycle-bin-trigger"
              >
                <Trash2 size={16} className="mr-1.5" />
                Recycle Bin
              </Button>
            )}
            {canDeleteCampaign && selectedRows.size > 0 && (
              <Button
                variant="destructive"
                className="animate-fadeIn"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 size={16} className="mr-1.5" />
                Delete ({selectedRows.size})
              </Button>
            )}

            <Button
              className="neon-left neon-button"
              variant="outline"
              onClick={handleExportExcel}
            >
              Export
            </Button>

            {canAddCampaign && (
              <Button className="bg-neon text-black hover:bg-neon/90">
                <Link to="/admin">+ New Campaigns</Link>
              </Button>
            )}
          </div>
        </div>

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        <Card className="neon-left neon-table">
          <CardContent className="p-2 pt-2 overflow-x-auto">
            <table
              className="text-[13px] border-collapse w-full"
              style={{ tableLayout: "fixed" }}
            >
              <thead>
                <tr className="border-b border-border">
                  <th
                    style={{ width: 36, minWidth: 36 }}
                    className="px-2 py-3 text-left"
                  >
                    <input
                      type="checkbox"
                      checked={
                        filteredRows.length > 0 &&
                        selectedRows.size === filteredRows.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows(
                            new Set(filteredRows.map((r) => r.id)),
                          );
                          setSelectAll(true);
                        } else {
                          setSelectedRows(new Set());
                          setSelectAll(false);
                        }
                      }}
                    />
                  </th>

                  {visibleColumns.map((c) => {
                    const w = getW(c);
                    const sortable = SORTABLE.has(c);
                    if (c === "CM") {
                      return (
                        <th
                          key={c}
                          style={{ width: w, minWidth: MIN_W }}
                          className="px-2 py-3 text-left font-medium relative group select-none"
                        >
                          <Popover open={cmFilterOpen} onOpenChange={setCmFilterOpen}>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className={`flex items-center gap-1 truncate pr-2 hover:text-foreground transition-colors ${cmFilter.size > 0 ? "text-primary font-semibold" : ""}`}
                                data-testid="cm-filter-trigger"
                              >
                                <span className="truncate">Campaign Manager</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                                {cmFilter.size > 0 && (
                                  <span className="ml-0.5 text-[10px] rounded-full bg-primary/15 text-primary px-1.5">
                                    {cmFilter.size}
                                  </span>
                                )}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-56 max-h-72 overflow-y-auto p-2">
                              <div className="flex items-center justify-between mb-1 px-1">
                                <span className="text-xs font-semibold text-muted-foreground">Filter by CM</span>
                                {cmFilter.size > 0 && (
                                  <button
                                    type="button"
                                    className="text-[11px] text-primary hover:underline"
                                    onClick={() => setCmFilter(new Set())}
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                              {uniqueCMs.length === 0 && (
                                <p className="text-xs text-muted-foreground px-1 py-2">No campaign managers found</p>
                              )}
                              {uniqueCMs.map((name) => (
                                <label
                                  key={name}
                                  className="flex items-center gap-2 text-sm py-1 px-1 rounded cursor-pointer hover:bg-muted/60"
                                >
                                  <input
                                    type="checkbox"
                                    checked={cmFilter.has(name)}
                                    onChange={(e) => {
                                      setCmFilter((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(name);
                                        else next.delete(name);
                                        return next;
                                      });
                                    }}
                                  />
                                  <span className="truncate">{name}</span>
                                </label>
                              ))}
                            </PopoverContent>
                          </Popover>
                          <div
                            className="absolute right-0 top-0 h-full w-2 flex items-center justify-center cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            onMouseDown={(e) => resizeMouseDown(e, c)}
                          >
                            <div className="h-4 w-px bg-border rounded-full" />
                          </div>
                        </th>
                      );
                    }
                    return (
                      <th
                        key={c}
                        style={{ width: w, minWidth: MIN_W }}
                        className="px-2 py-3 text-left font-medium relative group select-none"
                      >
                        <div
                          className={`flex items-center truncate pr-2 ${sortable ? "cursor-pointer hover:text-foreground transition-colors" : ""}`}
                          onClick={() => handleSort(c)}
                        >
                          <span className="truncate">
                            {formatColumnLabel(c)}
                          </span>
                          {sortable && (
                            <SortIndicator col={c} sortConfig={sortConfig} />
                          )}
                        </div>
                        <div
                          className="absolute right-0 top-0 h-full w-2 flex items-center justify-center cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          onMouseDown={(e) => resizeMouseDown(e, c)}
                        >
                          <div className="h-4 w-px bg-border rounded-full" />
                        </div>
                      </th>
                    );
                  })}

                  <th
                    style={{ width: getW("__progress__"), minWidth: MIN_W }}
                    className="px-2 py-3 text-left font-medium relative group select-none"
                  >
                    <span>Progress</span>
                    <div
                      className="absolute right-0 top-0 h-full w-2 flex items-center justify-center cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      onMouseDown={(e) => resizeMouseDown(e, "__progress__")}
                    >
                      <div className="h-4 w-px bg-border rounded-full" />
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {sortedRows.map((it) => {
                  const pct = it.pctToGoal ?? 0;
                  return (
                    <tr
                      key={it.id}
                      data-testid={`campaign-row-${it.id}`}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                    >
                      <td
                        style={{ width: 36 }}
                        className="px-2 py-2 align-middle"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRows.has(it.id)}
                          onChange={(e) => {
                            const s = new Set(selectedRows);
                            e.target.checked ? s.add(it.id) : s.delete(it.id);
                            setSelectedRows(s);
                            setSelectAll(s.size === filteredRows.length);
                          }}
                        />
                      </td>

                      {visibleColumns.map((c) => (
                        <td
                          key={c}
                          style={{ width: getW(c), maxWidth: getW(c) }}
                          className="px-2 py-2 align-middle overflow-hidden"
                        >
                          <div className="truncate w-full">
                            {renderCell(c, it)}
                          </div>
                        </td>
                      ))}

                      <td
                        style={{ width: getW("__progress__") }}
                        className="px-2 py-2 align-middle text-center font-semibold"
                      >
                        <span
                          className={
                            it.color === "green"
                              ? "text-emerald-600"
                              : it.color === "yellow"
                                ? "text-amber-500"
                                : it.color === "orange"
                                  ? "text-orange-500"
                                  : "text-rose-500"
                          }
                        >
                          {Math.min(100, pct)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Footer */}
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-xs text-muted-foreground">{rowCountLabel}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Show per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    const n = Number(v);
                    setPageSize(n);
                    setPage(1);
                    getData({ page: 1, pageSize: n });
                  }}
                >
                  <SelectTrigger className="w-[90px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[50, 100, 150, 200, 300, 400, 500].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent data-testid="campaign-detail-modal">
            <DialogHeader>
              <DialogTitle>{selected?.campaignName || "Details"}</DialogTitle>
            </DialogHeader>
          </DialogContent>
        </Dialog>
        <Dialog
  open={showDeleteDialog}
  onOpenChange={(o) => { if (!o) { setShowDeleteDialog(false); setDeleteConfirmText(""); } }}
>
  <DialogContent data-testid="delete-campaigns-modal">
    <DialogHeader>
      <DialogTitle className="text-red-600 dark:text-red-400">
        Move {selectedRows.size} Campaign{selectedRows.size !== 1 ? "s" : ""} to Recycle Bin?
      </DialogTitle>
    </DialogHeader>

    <div className="space-y-3 pt-1">
      <p className="text-sm text-muted-foreground">
        Deleted campaigns move to the Recycle Bin, where an admin can restore them or
        remove them permanently. Type{" "}
        <span className="font-semibold text-foreground">DELETE</span> below to confirm.
      </p>

      <div className="neon-input-wrap">
        <input
          type="text"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          placeholder="Type DELETE to confirm"
          className="neon-input w-full px-3 py-2 rounded-full text-sm"
          autoFocus
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          variant="outline"
          className="neon-left neon-button"
          onClick={() => { setShowDeleteDialog(false); setDeleteConfirmText(""); }}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={deleteConfirmText !== "DELETE" || deleting}
          onClick={handleBulkDelete}
        >
          {deleting ? "Moving…" : "Move to Recycle Bin"}
        </Button>
      </div>
    </div>
  </DialogContent>
</Dialog>

<RecycleBinDialog
  open={showRecycleBin}
  onOpenChange={setShowRecycleBin}
  canDelete={canDeleteCampaign}
  onChanged={() => getData({ page: 1 })}
/>
      </div>
      <Footer />
    </div>
  );
};
function App() {
  const logout = () => {
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  return (
    <div className="App">
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/otp" element={<OTP />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["Admin", "CSM","CSM,CM"]}>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute roles={["Admin"]}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaign/:id"
            element={
              <ProtectedRoute>
                <CampaignDetail />
              </ProtectedRoute>
            }
          />
          <Route path="/preferability" element={<Preferability />} />
          <Route
            path="/campaign/:id/edit"
            element={
              <ProtectedRoute roles={["CM", "admin","CSM","CSM,CM"]}>
                <CampaignEdit />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
